import { WalletBalance } from '@/components/billing/WalletBalance'
import { RequireAccess } from '@/components/require-access'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { centsToEuros } from '@/constants'
import { useJobillaOrgId } from '@/hooks'
import { useAuth, usePrivateChannel } from '@/lib'
import {
	AmountField,
	appendToastAtom,
	Button,
	FormDialog,
	LargeTextField,
	logger,
	SelectField,
	SettingsNav,
	SubtleLoader,
	TextField,
	useToastStoreContext,
} from '@/ui'
import {
	type BillingProfileData,
	broadcastChannel,
	customerWalletHoldsQueryKey,
	customerWalletShowQueryKey,
	CustomerWalletTransactionData,
	customerWalletTransactionsQueryKey,
	locationEnum,
	useCustomerValidateBillingDetails,
	useCustomerWalletCardTopup,
	useCustomerWalletHolds,
	useCustomerWalletInvoiceTopup,
	useCustomerWalletShow,
	useCustomerWalletTransactions,
	useCustomFieldList,
	useInvoiceSettingsShow,
	useProfilesIndex,
	walletEvent,
	WalletHoldData,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { useSetAtom } from 'jotai'
import { ChevronDownIcon, Loader2Icon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type TopUpMethod = 'invoice' | 'card'

export function Wallet() {
	const { t, i18n } = useTranslation('common')
	const { can } = useAuth()
	const organisationId = useJobillaOrgId()
	// Lets the header's Top up trigger restart the transactions poll schedule that
	// lives in WalletContent, without lifting that whole query up.
	const pollResetRef = useRef<(() => void) | null>(null)

	const wallet = useCustomerWalletShow(organisationId, {
		enabled: !!organisationId,
	})

	usePrivateChannel(broadcastChannel.OrganisationWallet({ organisation: organisationId }), {
		[walletEvent.Credited]: [customerWalletShowQueryKey, customerWalletTransactionsQueryKey],
		[walletEvent.Debited]: [customerWalletShowQueryKey, customerWalletTransactionsQueryKey],
		[walletEvent.Held]: [
			customerWalletShowQueryKey,
			customerWalletTransactionsQueryKey,
			customerWalletHoldsQueryKey,
		],
		[walletEvent.Settled]: [
			customerWalletShowQueryKey,
			customerWalletTransactionsQueryKey,
			customerWalletHoldsQueryKey,
		],
		[walletEvent.Released]: [
			customerWalletShowQueryKey,
			customerWalletTransactionsQueryKey,
			customerWalletHoldsQueryKey,
		],
	})

	const billingValidation = useCustomerValidateBillingDetails(organisationId, undefined, {
		enabled: !!organisationId,
	})
	const taxPercentage = billingValidation.data?.customer?.tax?.taxRate.percentage ?? 0

	const hasTrackedViewRef = useRef(false)
	useEffect(() => {
		if (hasTrackedViewRef.current || !wallet.data) { return }
		hasTrackedViewRef.current = true
		logger.track('Wallet: Settings page viewed', {
			data: {
				balance_cents: wallet.data.balance,
				pending_cents: wallet.data.pending,
			},
		})
	}, [wallet.data])

	const currencyFormatter = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: 'EUR',
	})

	return (
		<SettingsNav active="wallet" fullWidth={false}>
			<section className="space-y-8 mt-8 pr-8 max-w-[800px]">
				<div className="mt-10">
					<div className="flex items-start justify-between gap-4">
						<h2 className="text-2xl font-medium tracking-tight">
							{t('settings.wallet.title')}
						</h2>
						{wallet.data && (
							<div className="flex items-start gap-6">
								<div className="text-right">
									<div className="text-[10px] font-medium text-grey-500 uppercase tracking-wider">
										{t('settings.wallet.currentBalance')}
									</div>
									<WalletBalance
										balanceCents={wallet.data.balance}
										taxPercentage={taxPercentage}
										currencyFormatter={currencyFormatter}
										className="text-2xl font-semibold text-grey-900 tabular-nums leading-tight"
									/>
								</div>
								{wallet.data.pending > 0 && (
									<>
										<div className="w-px self-stretch bg-grey-200" aria-hidden="true" />
										<div className="text-right">
											<div className="text-[10px] font-medium text-grey-500 uppercase tracking-wider">
												{t('settings.wallet.pendingBalance')}
											</div>
											<WalletBalance
												balanceCents={wallet.data.pending}
												taxPercentage={taxPercentage}
												currencyFormatter={currencyFormatter}
												className="text-2xl font-semibold text-grey-900 tabular-nums leading-tight"
											/>
										</div>
									</>
								)}
							</div>
						)}
					</div>

					{can('billing:read')
						? (
							<div className="mt-4 flex h-px items-center">
								<div className="h-px flex-1 bg-grey-100" />
								<TopUpMenu onCardTopUpStarted={() => pollResetRef.current?.()} />
								<div className="h-px w-5 bg-grey-100" />
							</div>
						)
						: <div className="mt-4 h-px bg-grey-100" />}
				</div>

				<RequireAccess
					permission="billing:read"
					fallback={
						<p className="text-sm text-gray-500">
							{t('settings.wallet.noPermission')}
						</p>
					}
				>
					<WalletContent currencyFormatter={currencyFormatter} pollResetRef={pollResetRef} />
				</RequireAccess>
			</section>
		</SettingsNav>
	)
}

// Tail-off polling: fast at first, slows down, then stops after MAX_DURATION.
// Index N is the delay to wait BEFORE the (N+1)th fetch.
const POLL_SCHEDULE_MS = [5_000, 5_000, 5_000, 10_000, 10_000] as const
const POLL_LONG_INTERVAL_MS = 30_000
const POLL_MAX_DURATION_MS = 5 * 60 * 1000

function usePollSchedule() {
	const scheduleStartedAtRef = useRef<number>(Date.now())
	const tickCountRef = useRef<number>(0)
	// Bump to force React Query to re-read the refetchInterval closure after a reset.
	const [resetVersion, setResetVersion] = useState(0)

	const reset = useCallback(() => {
		scheduleStartedAtRef.current = Date.now()
		tickCountRef.current = 0
		setResetVersion((v) => v + 1)
	}, [])

	const registerTick = useCallback(() => {
		tickCountRef.current += 1
	}, [])

	const computeNextInterval = useCallback((): number | false => {
		const elapsedMs = Date.now() - scheduleStartedAtRef.current
		if (elapsedMs >= POLL_MAX_DURATION_MS) { return false }
		return POLL_SCHEDULE_MS[tickCountRef.current] ?? POLL_LONG_INTERVAL_MS
	}, [])

	return { reset, registerTick, computeNextInterval, resetVersion }
}

function WalletContent(
	{ currencyFormatter, pollResetRef }: {
		currencyFormatter: Intl.NumberFormat
		pollResetRef: { current: (() => void) | null }
	},
) {
	const organisationId = useJobillaOrgId()
	const { reset, registerTick, computeNextInterval, resetVersion } = usePollSchedule()

	const transactions = useCustomerWalletTransactions(organisationId, {
		enabled: !!organisationId,
		// Recreate the function whenever the schedule is reset so React Query picks it up.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		refetchInterval: useMemo(() => () => computeNextInterval(), [computeNextInterval, resetVersion]),
		refetchIntervalInBackground: false,
	})

	useEffect(() => {
		if (!transactions.dataUpdatedAt) { return }
		registerTick()
	}, [transactions.dataUpdatedAt, registerTick])

	const handleCardTopUpStarted = useCallback(() => {
		reset()
		// Force an immediate refetch so the new schedule kicks in even if polling had stopped.
		transactions.refetch()
	}, [reset, transactions])

	// Expose the poll reset so the header's Top up trigger can restart polling.
	useEffect(() => {
		pollResetRef.current = handleCardTopUpStarted
	}, [handleCardTopUpStarted, pollResetRef])

	if (transactions.isLoading || !transactions.data) {
		return <SubtleLoader delay={0} />
	}

	return (
		<div className="space-y-8">
			<HoldsList currencyFormatter={currencyFormatter} />

			<TransactionsList
				transactions={transactions.data.data}
				currencyFormatter={currencyFormatter}
				isRefetching={transactions.isFetching}
			/>
		</div>
	)
}

function TopUpMenu({ onCardTopUpStarted }: { onCardTopUpStarted: () => void }) {
	const { t } = useTranslation('common')
	const { can } = useAuth()
	const organisationId = useJobillaOrgId()
	const toastStore = useToastStoreContext()
	const appendToast = useSetAtom(appendToastAtom, { store: toastStore })
	const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
	const [profileDialogOpen, setProfileDialogOpen] = useState(false)

	const billingValidation = useCustomerValidateBillingDetails(organisationId, undefined, {
		enabled: !!organisationId,
	})
	const invoiceSettings = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId,
	})

	const profilesQuery = useProfilesIndex(organisationId, {
		enabled: !!organisationId && can('profile:view'),
	})
	const profiles = useMemo(() => profilesQuery.data?.data ?? [], [profilesQuery.data])

	const isBillingComplete = billingValidation.data?.validation.valid ?? false
	const hasCustomer = !!billingValidation.data?.customer
	const canPayViaInvoice = invoiceSettings.data?.canPayViaInvoice ?? false
	const canRequestInvoiceTopup = can('wallet:request_invoice_topup')
	const canCardTopup = can('wallet:card_topup')

	const cardTopupMutation = useCustomerWalletCardTopup({
		mutationKey: ['wallet-settings-topup-card'],
		onSuccess: (data) => {
			setProfileDialogOpen(false)
			onCardTopUpStarted()
			window.open(new URL(data.url), '_blank')
		},
		onError: (err) => {
			logger.error(err, { tags: { component: 'WalletSettings', action: 'wallet-topup-card' } })
			appendToast({
				title: t('settings.wallet.errorTitle'),
				description: err?.response?.data?.message || t('settings.wallet.tryAgain'),
				variant: 'error',
				cause: err,
			})
		},
	})

	const startCardTopUp = (profileId?: string) => {
		logger.track('Wallet: Card top-up opened', { data: { chose_profile: profileId !== undefined } })
		cardTopupMutation.mutate({
			organisation: organisationId,
			data: { returnUrl: window.location.href, profileId: profileId ?? null },
		})
	}

	const handleInvoiceClick = () => {
		logger.track('Wallet: Invoice top-up flow opened')
		setInvoiceDialogOpen(true)
	}

	// With more than one billing profile the user must choose which one funds the
	// top-up; with zero or one the backend uses the only/primary customer.
	const handleCardClick = () => {
		if (profiles.length > 1) {
			setProfileDialogOpen(true)
			return
		}
		startCardTopUp()
	}

	const invoiceDisabled = !canRequestInvoiceTopup || !canPayViaInvoice || !isBillingComplete
	const invoiceReason = !canRequestInvoiceTopup
		? t('settings.wallet.topup.invoicePermissionLocked')
		: !canPayViaInvoice
		? t('settings.wallet.topup.invoiceLocked')
		: !isBillingComplete
		? t('settings.wallet.billingRequiredDescription')
		: undefined
	const cardDisabled = !canCardTopup || !hasCustomer || !isBillingComplete
	const cardReason = !canCardTopup
		? t('settings.wallet.topup.cardPermissionLocked')
		: !hasCustomer
		? t('settings.wallet.topup.cardNoCustomer')
		: !isBillingComplete
		? t('settings.wallet.billingRequiredDescription')
		: undefined

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className="flex items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer data-[state=open]:border-brand-500 data-[state=open]:text-brand-700"
					>
						{t('settings.wallet.topup.button')}
						<ChevronDownIcon className="h-3.5 w-3.5" />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="min-w-44 max-w-64">
					<DropdownMenuItem
						disabled={invoiceDisabled}
						onSelect={handleInvoiceClick}
						className="flex cursor-pointer flex-col items-start gap-0.5 text-grey-900 focus:bg-grey-50 focus:text-grey-900"
					>
						<span className="text-sm font-medium">{t('settings.wallet.topup.invoiceOption')}</span>
						{invoiceDisabled && invoiceReason && (
							<span className="text-xs leading-snug text-grey-500">{invoiceReason}</span>
						)}
					</DropdownMenuItem>
					<DropdownMenuItem
						disabled={cardDisabled}
						onSelect={handleCardClick}
						className="flex cursor-pointer flex-col items-start gap-0.5 text-grey-900 focus:bg-grey-50 focus:text-grey-900"
					>
						<span className="text-sm font-medium">{t('settings.wallet.topup.cardOption')}</span>
						{cardDisabled && cardReason && (
							<span className="text-xs leading-snug text-grey-500">{cardReason}</span>
						)}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<InvoiceTopUpDialog
				open={invoiceDialogOpen}
				profiles={profiles}
				onClose={() => setInvoiceDialogOpen(false)}
			/>

			<CardTopUpProfileDialog
				open={profileDialogOpen}
				profiles={profiles}
				isSubmitting={cardTopupMutation.isPending}
				error=""
				onConfirm={startCardTopUp}
				onClose={() => {
					if (cardTopupMutation.isPending) {
						return
					}
					setProfileDialogOpen(false)
				}}
			/>
		</>
	)
}

function profileLabel(profile: BillingProfileData): string {
	return profile.name ?? profile.stripeCustomerId
}

function CardTopUpProfileDialog(
	{ open, profiles, isSubmitting, error, onConfirm, onClose }: {
		open: boolean
		profiles: BillingProfileData[]
		isSubmitting: boolean
		error: string
		onConfirm: (profileId: string) => void
		onClose: () => void
	},
) {
	const { t } = useTranslation('common')
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)

	// Default the selection to the primary profile each time the dialog opens.
	useEffect(() => {
		if (!open) {
			setSelectedProfileId(null)
			return
		}
		if (selectedProfileId !== null || profiles.length === 0) {
			return
		}
		const primary = profiles.find((profile) => profile.isPrimary)
		setSelectedProfileId((primary ?? profiles[0]).id)
	}, [open, profiles, selectedProfileId])

	const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId) ?? null

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={t('settings.wallet.topup.cardProfileDialog.title')}
			maxWidth="md"
		>
			<div className="space-y-5">
				<p className="text-sm text-grey-600">
					{t('settings.wallet.topup.cardProfileDialog.description')}
				</p>

				{selectedProfile && (
					<SelectField<BillingProfileData>
						label={t('settings.wallet.topup.cardProfileDialog.profileLabel')}
						options={profiles}
						value={selectedProfile}
						onChange={(profile) => setSelectedProfileId(profile.id)}
						disabled={isSubmitting}
						renderOption={(profile) => (
							<span className="flex items-center gap-2">
								<span>{profileLabel(profile)}</span>
								{profile.isPrimary
									? <span className="text-xs text-grey-500">{t('profiles.primary')}</span>
									: null}
							</span>
						)}
					/>
				)}

				{error && (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
						<div className="font-medium text-amber-900 mb-1">
							{t('settings.wallet.errorTitle')}
						</div>
						<div className="text-amber-700">{error}</div>
					</div>
				)}

				<div className="flex justify-end gap-2 pt-2 border-t border-grey-100 -mx-6 px-6 pt-4 mt-2">
					<Button
						type="subtle"
						size="short"
						onClick={onClose}
						disabled={isSubmitting}
					>
						{t('settings.wallet.topup.cardProfileDialog.cancel')}
					</Button>
					<Button
						type="committal"
						size="short"
						onClick={() => selectedProfile && onConfirm(selectedProfile.id)}
						disabled={!selectedProfile || isSubmitting}
						isLoading={isSubmitting}
					>
						{t('settings.wallet.topup.cardProfileDialog.confirm')}
					</Button>
				</div>
			</div>
		</FormDialog>
	)
}

const INVOICE_AMOUNT_PRESETS_EUROS = [100, 500, 1000, 5000] as const
const INVOICE_MIN_AMOUNT_EUROS = 100

const INVOICE_CUSTOM_FIELD_MAX_LENGTH = 140

function InvoiceTopUpDialog(
	{ open, profiles, onClose }: { open: boolean; profiles: BillingProfileData[]; onClose: () => void },
) {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const [amountEuros, setAmountEuros] = useState<number>(0)
	const [note, setNote] = useState('')
	const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({})
	const [submitError, setSubmitError] = useState('')
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)

	// With more than one billing profile the user picks which one the draft
	// invoice is raised against; with zero or one the backend uses the
	// only/primary customer. Default to the primary each time the dialog opens.
	const showProfileChooser = profiles.length > 1
	useEffect(() => {
		if (!open) {
			setSelectedProfileId(null)
			return
		}
		if (selectedProfileId !== null || profiles.length === 0) {
			return
		}
		const primary = profiles.find((profile) => profile.isPrimary)
		setSelectedProfileId((primary ?? profiles[0]).id)
	}, [open, profiles, selectedProfileId])

	const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId) ?? null

	const currencyFormatter = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: 'EUR',
	})

	const customFieldsQuery = useCustomFieldList(organisationId, locationEnum.invoice, {
		enabled: !!organisationId,
	})
	const customFields = customFieldsQuery.data?.fields ?? []

	const setCustomFieldValue = useCallback((fieldId: string, value: string) => {
		setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }))
	}, [])

	const isMissingRequiredCustomField = customFields
		.filter((field) => field.isRequired)
		.some((field) => !customFieldValues[field.id]?.trim())

	const hasOverlongCustomField = Object.values(customFieldValues).some(
		(value) => value.length > INVOICE_CUSTOM_FIELD_MAX_LENGTH,
	)

	const mutation = useCustomerWalletInvoiceTopup({
		mutationKey: ['wallet-settings-invoice-topup'],
		onSuccess: (_data, variables) => {
			const filledCustomFieldCount = Array.isArray(variables.data.customFields)
				? variables.data.customFields.length
				: 0
			logger.track('Wallet: Invoice top-up requested', {
				data: {
					amount_cents: variables.data.amount,
					has_note: variables.data.note !== null,
					custom_field_count: filledCustomFieldCount,
				},
			})
			queryClient.invalidateQueries({ queryKey: customerWalletShowQueryKey(organisationId) })
			setAmountEuros(0)
			setNote('')
			setCustomFieldValues({})
			setSubmitError('')
			onClose()
		},
		onError: (err) => {
			logger.error(err, { tags: { component: 'WalletSettings', action: 'wallet-invoice-topup' } })
			setSubmitError(err?.response?.data?.message || t('settings.wallet.tryAgain'))
		},
		onMutate: () => {
			setSubmitError('')
		},
	})

	const handleUserCancel = () => {
		if (mutation.isPending) { return }
		logger.track('Wallet: Invoice top-up flow closed')
		onClose()
	}

	const handleSubmit = () => {
		const amountCents = Math.round(amountEuros * 100)
		const filledCustomFields = Object.entries(customFieldValues)
			.filter(([, value]) => value.trim() !== '')
			.map(([id, value]) => ({ id, value }))

		mutation.mutate({
			organisation: organisationId,
			data: {
				amount: amountCents,
				note: note.trim() === '' ? null : note.trim(),
				customFields: filledCustomFields.length > 0 ? filledCustomFields : null,
				profileId: showProfileChooser && selectedProfile ? selectedProfile.id : null,
			},
		})
	}

	const isValidAmount = amountEuros >= INVOICE_MIN_AMOUNT_EUROS
	const minimumFormatted = currencyFormatter.format(INVOICE_MIN_AMOUNT_EUROS)
	const amountHint = isValidAmount
		? ''
		: t('settings.wallet.topup.invoiceDialog.amountHint', { minimum: minimumFormatted })

	const isFormInvalid = !isValidAmount || isMissingRequiredCustomField || hasOverlongCustomField
	const isSubmitDisabled = isFormInvalid || mutation.isPending

	return (
		<FormDialog
			open={open}
			onClose={handleUserCancel}
			title={t('settings.wallet.topup.invoiceDialog.title')}
			maxWidth="lg"
		>
			<div className="space-y-5">
				<p className="text-sm text-grey-600">
					{t('settings.wallet.topup.invoiceDialog.description')}
				</p>

				{showProfileChooser && selectedProfile && (
					<SelectField<BillingProfileData>
						label={t('settings.wallet.topup.invoiceDialog.profileLabel')}
						options={profiles}
						value={selectedProfile}
						onChange={(profile) => setSelectedProfileId(profile.id)}
						disabled={mutation.isPending}
						renderOption={(profile) => (
							<span className="flex items-center gap-2">
								<span>{profileLabel(profile)}</span>
								{profile.isPrimary
									? <span className="text-xs text-grey-500">{t('profiles.primary')}</span>
									: null}
							</span>
						)}
					/>
				)}

				<div className="space-y-2">
					<AmountField
						label={t('settings.wallet.topup.invoiceDialog.amountLabel')}
						value={amountEuros}
						onChange={setAmountEuros}
						hint={amountHint}
						locale={i18n.language}
						prefix="€"
						suffix="+ VAT"
					/>
					<div className="flex flex-wrap gap-2">
						{INVOICE_AMOUNT_PRESETS_EUROS.map((preset) => {
							const selected = amountEuros === preset
							return (
								<button
									key={preset}
									type="button"
									onClick={() => {
										setAmountEuros(preset)
										logger.track('Wallet: Invoice amount preset selected', {
											data: { amount_cents: preset * 100 },
										})
									}}
									className={`text-xs font-medium tabular-nums rounded-full px-3 py-1 border transition-colors
										${
										selected
											? 'border-brand-500 bg-brand-50 text-brand-700'
											: 'border-grey-200 text-grey-700 hover:border-grey-300 hover:bg-grey-50'
									}`}
								>
									{currencyFormatter.format(preset)}
								</button>
							)
						})}
					</div>
				</div>

				{customFields.length > 0 && (
					<div className="space-y-3">
						<div className="text-sm font-medium text-grey-700">
							{t('settings.customFields.title')}
						</div>
						{customFields.map((field) => {
							const value = customFieldValues[field.id] ?? ''
							const isEmpty = value.trim() === ''
							const isTooLong = value.length > INVOICE_CUSTOM_FIELD_MAX_LENGTH
							const errors = [
								...(field.isRequired && isEmpty
									? [t('settings.customFields.errors.required')]
									: []),
								...(isTooLong
									? [t('settings.customFields.errors.maxlength', {
										max: INVOICE_CUSTOM_FIELD_MAX_LENGTH,
									})]
									: []),
							]
							return (
								<TextField
									key={field.id}
									label={field.name}
									value={value}
									onChange={(v) => setCustomFieldValue(field.id, v)}
									hint={field.isRequired
										? t('settings.customFields.required')
										: t('settings.customFields.optional')}
									errors={errors}
									maxLength={INVOICE_CUSTOM_FIELD_MAX_LENGTH}
								/>
							)
						})}
					</div>
				)}

				<div className="relative">
					<span className="absolute right-0 top-0 text-sm text-grey-500 pointer-events-none z-10">
						{t('settings.customFields.optional')}
					</span>
					<LargeTextField
						label={t('settings.wallet.topup.invoiceDialog.noteLabel')}
						value={note}
						onChange={setNote}
						hint={t('settings.wallet.topup.invoiceDialog.noteHint')}
					/>
				</div>

				{submitError && (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
						<div className="font-medium text-amber-900 mb-1">
							{t('settings.wallet.errorTitle')}
						</div>
						<div className="text-amber-700">{submitError}</div>
					</div>
				)}

				<div className="flex justify-end gap-2 pt-2 border-t border-grey-100 -mx-6 px-6 pt-4 mt-2">
					<Button
						type="subtle"
						size="short"
						onClick={handleUserCancel}
						disabled={mutation.isPending}
					>
						{t('settings.wallet.topup.invoiceDialog.cancel')}
					</Button>
					<div
						className={isFormInvalid
							? 'grayscale opacity-60 transition-[filter,opacity]'
							: 'transition-[filter,opacity]'}
					>
						<Button
							type="committal"
							size="short"
							onClick={handleSubmit}
							disabled={isSubmitDisabled}
							isLoading={mutation.isPending}
						>
							{t('settings.wallet.topup.invoiceDialog.submit')}
						</Button>
					</div>
				</div>
			</div>
		</FormDialog>
	)
}

function TransactionsList(
	{ transactions, currencyFormatter, isRefetching = false }: {
		transactions: CustomerWalletTransactionData[]
		currencyFormatter: Intl.NumberFormat
		isRefetching?: boolean
	},
) {
	const { t, i18n } = useTranslation('common')
	const accountingFormatter = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: 'EUR',
		currencySign: 'accounting',
	})

	const header = (
		<div className="flex items-center justify-between mb-3">
			<div className="text-sm font-medium text-grey-700">
				{t('settings.wallet.transactions.title')}
			</div>
			{isRefetching && (
				<Loader2Icon
					className="size-4 text-grey-400 animate-spin"
					aria-label={t('settings.wallet.transactions.refreshing')}
				/>
			)}
		</div>
	)

	if (transactions.length === 0) {
		return (
			<div>
				{header}
				<div className="border border-grey-100 rounded-lg p-8 text-center">
					<div className="text-sm text-grey-500">
						{t('settings.wallet.transactions.emptyState')}
					</div>
				</div>
			</div>
		)
	}

	return (
		<div>
			{header}
			<div className="border border-grey-100 rounded-lg overflow-hidden">
				<table className="w-full text-sm">
					<thead className="bg-grey-50 text-left text-xs font-medium text-grey-600 uppercase tracking-wider">
						<tr>
							<th className="px-4 py-2.5">{t('settings.wallet.transactions.list.date')}</th>
							<th className="px-4 py-2.5 text-right">{t('settings.wallet.transactions.list.amount')}</th>
							<th className="px-4 py-2.5 text-right">
								{t('settings.wallet.transactions.list.balance')}
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-grey-100">
						{groupByMonth(transactions, i18n.language).map((entry) =>
							entry.type === 'header'
								? (
									<tr key={`h-${entry.key}`} className="bg-grey-50/60">
										<td
											colSpan={3}
											className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-grey-500"
										>
											{entry.label}
										</td>
									</tr>
								)
								: (
									<TransactionRow
										key={entry.transaction.id}
										transaction={entry.transaction}
										currencyFormatter={currencyFormatter}
										accountingFormatter={accountingFormatter}
									/>
								)
						)}
					</tbody>
				</table>
			</div>
		</div>
	)
}

type GroupedTransactionEntry =
	| { type: 'header'; key: string; label: string }
	| { type: 'transaction'; transaction: CustomerWalletTransactionData }

function groupByMonth(
	transactions: CustomerWalletTransactionData[],
	locale: string,
): GroupedTransactionEntry[] {
	const formatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
	const entries: GroupedTransactionEntry[] = []
	let lastKey = ''

	for (const transaction of transactions) {
		const createdAt = parseISO(transaction.createdAt)
		const key = `${createdAt.getFullYear()}-${createdAt.getMonth()}`

		if (key !== lastKey) {
			entries.push({ type: 'header', key, label: formatter.format(createdAt) })
			lastKey = key
		}
		entries.push({ type: 'transaction', transaction })
	}

	return entries
}

function TransactionRow(
	{ transaction, currencyFormatter, accountingFormatter }: {
		transaction: CustomerWalletTransactionData
		currencyFormatter: Intl.NumberFormat
		accountingFormatter: Intl.NumberFormat
	},
) {
	const createdAt = parseISO(transaction.createdAt)
	const signedAmount = centsToEuros(transaction.amount) * (transaction.type === 'credit' ? 1 : -1)

	return (
		<tr className="text-grey-800">
			<td className="px-4 py-2.5 whitespace-nowrap">{format(createdAt, 'd MMM yyyy')}</td>
			<td className="px-4 py-2.5 text-right tabular-nums font-medium">
				{accountingFormatter.format(signedAmount)}
			</td>
			<td className="px-4 py-2.5 text-right tabular-nums text-grey-600">
				{currencyFormatter.format(centsToEuros(transaction.runningBalance))}
			</td>
		</tr>
	)
}

function HoldsList({ currencyFormatter }: { currencyFormatter: Intl.NumberFormat }) {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()

	const holds = useCustomerWalletHolds(organisationId, {
		enabled: !!organisationId,
	})

	if (holds.isLoading || !holds.data) {
		return null
	}
	if (holds.data.data.length === 0) {
		return null
	}

	return (
		<div>
			<div className="text-sm font-medium text-grey-700 mb-3">
				{t('settings.wallet.holds.title')}
			</div>
			<div className="border border-grey-100 rounded-lg overflow-hidden">
				<table className="w-full text-sm">
					<thead className="bg-grey-50 text-left text-xs font-medium text-grey-600 uppercase tracking-wider">
						<tr>
							<th className="px-4 py-2.5">{t('settings.wallet.holds.list.date')}</th>
							<th className="px-4 py-2.5">{t('settings.wallet.holds.list.heldFor')}</th>
							<th className="px-4 py-2.5 text-right">{t('settings.wallet.holds.list.amount')}</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-grey-100">
						{holds.data.data.map((hold) => (
							<HoldRow
								key={hold.id}
								hold={hold}
								currencyFormatter={currencyFormatter}
							/>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}

function HoldRow({ hold, currencyFormatter }: {
	hold: WalletHoldData
	currencyFormatter: Intl.NumberFormat
}) {
	const { t } = useTranslation('common')
	const heldAt = parseISO(hold.heldAt)

	return (
		<tr className="text-grey-800">
			<td className="px-4 py-2.5 whitespace-nowrap">{format(heldAt, 'd MMM yyyy')}</td>
			<td className="px-4 py-2.5 text-grey-700">
				{hold.description ?? t('settings.wallet.holds.unknownHoldable')}
			</td>
			<td className="px-4 py-2.5 text-right tabular-nums font-medium">
				{currencyFormatter.format(centsToEuros(hold.amount))}
			</td>
		</tr>
	)
}
